import { useEffect, useRef, useState } from 'react';
import { FormField, FormSchema, Option } from '../types';
import { formBuilderApi } from '../api';
import { getDataSourceError } from './errors';
import { sameValue } from './validate';

export function useFieldData(field: FormField, values: Record<string, unknown>, update: (value: unknown) => void, runtimeFormId?: string, dataSourceDraft: boolean | FormSchema = false, onOptions?: (options: Option[]) => void, visibleKeys?: Set<string>, active = true, onDynamicFields?: (fields: FormField[]) => void, cachedDynamicFields: FormField[] = []) {
  const source = field.data_source;
  const [options, setOptions] = useState<Option[]>(field.options || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Dynamic groups unmount when the user changes steps. Hydrate from the
  // renderer-level cache so generated fields survive step navigation.
  const [dynamicFields, setDynamicFields] = useState<FormField[]>(cachedDynamicFields);
  const onDynamicFieldsRef = useRef(onDynamicFields);
  onDynamicFieldsRef.current = onDynamicFields;
  const dependencies = source?.trigger.depends_on || [];
  const triggerFields = source?.trigger.dependency_mode === 'selected'
    ? (source.trigger.trigger_fields || [])
    : dependencies;
  const trackedFields = Array.from(new Set([...dependencies, ...triggerFields]));
  const dependencyValues = trackedFields.map((key) => values[key]);
  const previousValuesRef = useRef<Record<string, unknown>>({});
  const changedSinceRequestRef = useRef<Set<string>>(new Set());
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // Lock the field the moment a tracked dependency changes, not after the
    // debounce, so stale options can't be picked while a refresh is pending.
    // resolve() clears this on every path that ends up not fetching.
    const dependencyDriven = source?.trigger.type === 'dependency_change' || source?.trigger.type === 'step_enter_and_dependency_change';
    if (source && dependencyDriven && runtimeFormId && active && hasInitializedRef.current
      && trackedFields.some((key) => !sameValue(previousValuesRef.current[key], values[key]))) {
      setLoading(true);
    }
    const resolve = async () => {
      setError(undefined);
      if (!source) {
        setDynamicFields([]);
        onDynamicFieldsRef.current?.([]);
        setOptions(field.options || []);
        setLoading(false);
        return;
      }
      if (source.trigger.type === 'manual' || !runtimeFormId) {
        // Do not discard cached generated fields just because this step remounted.
        setDynamicFields(cachedDynamicFields);
        setLoading(false);
        return;
      }

      // By default, inactive fields do not resolve their data source. This is
      // intentionally checked before dependency bookkeeping so that becoming
      // active is itself enough to trigger the first request.
      if (!active && source.trigger.execute_when_inactive !== true) {
        // Inactive means "do not fetch", not "forget the generated schema".
        setDynamicFields(cachedDynamicFields);
        setLoading(false);
        return;
      }

      const triggerType = source.trigger.type;
      const firstRun = !hasInitializedRef.current;
      const dependencyTrigger =
        triggerType === 'dependency_change' ||
        triggerType === 'step_enter_and_dependency_change';
      const stepEnterTrigger =
        triggerType === 'step_enter' ||
        triggerType === 'step_enter_and_dependency_change';

      // A step-enter trigger is tied to the step containing this field. The field
      // is mounted when that step becomes active, so the first effect execution is
      // the step-entry event. It must not re-run merely because unrelated form values
      // change while the user remains on the same step.
      if ((triggerType === 'step_enter' || triggerType === 'form_load') && !firstRun) {
        setLoading(false);
        return;
      }

      if (dependencyTrigger) {
        const previous = previousValuesRef.current;
        const changed = new Set(changedSinceRequestRef.current);
        trackedFields.forEach((key) => {
          if (!hasInitializedRef.current || !sameValue(previous[key], values[key])) changed.add(key);
        });
        previousValuesRef.current = trackedFields.reduce<Record<string, unknown>>((acc, key) => { acc[key] = values[key]; return acc; }, {});
        hasInitializedRef.current = true;

        // For the combined trigger, entering the step is always enough for the
        // initial request. After that, dependency rules control refreshes.
        const initialStepEntry = stepEnterTrigger && firstRun;
        if (!initialStepEntry) {
          // A dependency being hidden is not a blocker. Only currently visible trigger
          // fields participate in the trigger decision; hidden fields are simply ignored.
          const visibleTriggers = triggerFields.filter((key) => !visibleKeys || visibleKeys.has(key));
          if (visibleTriggers.length === 0) {
            changedSinceRequestRef.current = changed;
            setLoading(false);
            return;
          }

          const visibleChanged = visibleTriggers.filter((key) => changed.has(key));
          const mode = source.trigger.dependency_mode || 'any';
          const shouldResolve =
            mode === 'all'
              ? visibleTriggers.every((key) => changed.has(key))
              : visibleChanged.length > 0;

          if (!shouldResolve) {
            changedSinceRequestRef.current = changed;
            setLoading(false);
            return;
          }
        }
        changedSinceRequestRef.current = new Set();
      } else if (stepEnterTrigger || triggerType === 'form_load') {
        // The initial mount is the lifecycle event for step_enter/form_load.
        hasInitializedRef.current = true;
        previousValuesRef.current = trackedFields.reduce<Record<string, unknown>>((acc, key) => { acc[key] = values[key]; return acc; }, {});
      }

      setLoading(true);
      try {
        const result = await formBuilderApi.resolveDataSource(runtimeFormId, field.key, values, dataSourceDraft);
        if (cancelled) return;
        if (source.response.type === 'options') { setDynamicFields([]); onDynamicFieldsRef.current?.([]); const nextOptions = result.options || []; setOptions(nextOptions); onOptions?.(nextOptions); }
        else if (source.response.type === 'value') { setDynamicFields([]); onDynamicFieldsRef.current?.([]); update(result.value); }
        else if (source.response.type === 'raw') { setDynamicFields([]); onDynamicFieldsRef.current?.([]); }
        else if (source.response.type === 'fields') { const nextFields = result.fields || []; setDynamicFields(nextFields); onDynamicFieldsRef.current?.(nextFields); }
      } catch (requestError) {
        if (!cancelled) {
          setOptions([]);
          setDynamicFields([]);
          onDynamicFieldsRef.current?.([]);
          setError(getDataSourceError(requestError));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const delay = source?.trigger.type === 'dependency_change' || source?.trigger.type === 'step_enter_and_dependency_change' ? source.trigger.debounce_ms : 0;
    const timer = window.setTimeout(resolve, delay);
    return () => { cancelled = true; window.clearTimeout(timer); };
  // Only declared dependency values are reactive inputs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.key, source, JSON.stringify(field.options), ...dependencyValues, JSON.stringify(visibleKeys ? Array.from(visibleKeys).sort() : []), active, source?.trigger.execute_when_inactive]);

  return { options, loading, error, dynamicFields };
}
